import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from 'react-icons/fi';
import { useBranding } from '../contexts/BrandingContext';
import OrganisationMark from '../components/OrganisationMark';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { branding } = useBranding();

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      const code = String(err?.code || '');
      setError(code.includes('invalid-credential') || code.includes('wrong-password') ? 'The email or password is incorrect.' : 'Unable to sign in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-brand-panel">
        <div className="relative z-10 flex h-full flex-col justify-between p-8 sm:p-12 lg:p-16">
          <div>
            <img src="/brand/pravah-wordmark.png" alt="Pravah" className="h-auto w-full max-w-[420px] object-contain object-left" />
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-[#F7F3E8]/80">Your Practice. In Flow. With Pravah.</p>
          </div>
          <div className="hidden max-w-lg lg:block">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#C8A96B]">Built for professional firms</p>
            <p className="mt-3 text-2xl font-medium leading-snug text-[#F7F3E8]">Assignments, matters, billing and office operations—connected in one clear workflow.</p>
          </div>
          <p className="text-xs text-[#F7F3E8]/50">© {new Date().getFullYear()} Pravah. All rights reserved.</p>
        </div>
      </section>

      <section className="login-form-panel">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center lg:text-left">
            {branding.showOrganisationLogo !== false && (
              <OrganisationMark
                branding={branding}
                className="mx-auto mb-5 max-h-20 max-w-[220px] object-contain lg:mx-0"
                fallbackClassName="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-[#0B1F33] text-xl font-bold text-[#F7F3E8] lg:mx-0"
              />
            )}
            <p className="login-organisation-name">{branding.organisationName}</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{branding.loginHeading || 'Welcome back'}</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{branding.loginSubheading || 'Sign in to continue to your workspace.'}</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <label className="ui-field">
              <span>Email address</span>
              <div className="input-with-icon">
                <FiMail />
                <input type="email" autoComplete="email" placeholder="name@firm.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </label>
            <label className="ui-field">
              <span>Password</span>
              <div className="input-with-icon">
                <FiLock />
                <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="button" className="password-toggle" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <FiEyeOff /> : <FiEye />}</button>
              </div>
            </label>
            {error && <div className="ui-alert ui-alert-danger">{error}</div>}
            <button disabled={submitting} className="ui-btn ui-btn-primary w-full justify-center py-3" type="submit">
              {submitting ? 'Signing in…' : <><span>Sign in</span><FiArrowRight /></>}
            </button>
          </form>
          {branding.supportEmail && <p className="mt-6 text-center text-xs text-slate-500">Need help? Contact <a className="font-semibold text-[#0B1F33]" href={`mailto:${branding.supportEmail}`}>{branding.supportEmail}</a></p>}
        </div>
      </section>
    </main>
  );
}
